import {reactive} from 'vue';
import {initializePaddleSingleton, setPaddleCustomer} from './paddle';
import type {AuthSession} from '@supabase/supabase-js';
import {supabaseAuthStorageKey} from '/src/supabase-config';
import {identifyPostHogUser} from '/src/posthog';

export type TAppSessionState = {
    auth: AuthSession;
    subscription: any;
    isInitialized: boolean;
};

export function hasSupabaseAuthRedirect(search: string, hash: string): boolean {
    return new URLSearchParams(search).has('code') || new URLSearchParams(hash.slice(1)).has('access_token');
}

class AppSession {
    state: TAppSessionState = reactive({
        auth: null,
        subscription: null,
        isInitialized: false,
    });

    private bootstrapPromise: Promise<void> | null = null;

    initialize = (): Promise<void> => {
        this.bootstrapPromise ??= this.bootstrap();
        return this.bootstrapPromise;
    };

    initializePaddle = () => {
        return initializePaddleSingleton();
    };

    resetAuth = () => {
        this.state.auth = null;
        this.state.subscription = null;
    };

    refreshSubscription = async (userId: string = this.state.auth?.user?.id) => {
        if (!userId) {
            this.state.subscription = null;
            return null;
        }
        const {getUserSubscription} = await import('/src/api/users');
        const subscription = await getUserSubscription(userId);
        this.state.subscription = subscription;
        if (subscription?.customer_id) {
            setPaddleCustomer(subscription.customer_id).catch((): undefined => undefined);
        }
        return subscription;
    };

    private async bootstrap(): Promise<void> {
        // Skip auth initialization when no Supabase auth token exists in storage.
        // This avoids loading the Supabase SDK (~213 KB) for anonymous visitors.
        // Always run when an auth redirect is present in the URL
        // so PKCE codes or magic-link hash tokens can become a session.
        const hasAuthToken = !!localStorage.getItem(supabaseAuthStorageKey);
        const hasAuthRedirect =
            typeof window !== 'undefined' && hasSupabaseAuthRedirect(window.location.search, window.location.hash);
        if (!hasAuthToken && !hasAuthRedirect) {
            this.state.isInitialized = true;
            return;
        }

        const {getSession} = await import('/src/api/services/supabase');
        const session = await getSession();
        this.state.auth = session;
        if (session) {
            identifyPostHogUser(session.user.id);
            await this.refreshSubscription(session.user.id);
        } else {
            this.state.subscription = null;
        }
        this.state.isInitialized = true;
    }
}

const appSession = new AppSession();

export function useAppSession() {
    void appSession.initialize();
    return appSession;
}
